/**
 * Putting the theme on the page. The colour maths is in theme.ts, which stays
 * free of the DOM so it can be tested (and imported) anywhere.
 */
import { DEFAULT_BRAND, HEX, primaryCss, seedOf } from './theme.ts';

/** Read by public/theme-boot.js before the first paint. Keep the two in step. */
const STORAGE_KEY = 'kijisaku.theme';

let applied: string | null | undefined;

/**
 * Put a theme on the whole application, and remember it for the next load.
 *
 * Remembered in localStorage so theme-boot.js can apply it before the first
 * paint - otherwise every page load would flash the default colour until the
 * session arrived. Only the two numbers are stored, and the boot script accepts
 * nothing but numbers, so the cache cannot be used to put arbitrary CSS on the
 * page. Idempotent, and a no-op when nothing changed.
 */
export function applyTheme(hex: string | null): void {
  const value = hex && HEX.test(hex) ? hex.toLowerCase() : null;
  if (value === applied) return;
  applied = value;

  const root = document.documentElement.style;
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  try {
    if (value) {
      const { h, c } = seedOf(value);
      root.setProperty('--brand-h', String(h));
      root.setProperty('--brand-c', String(c));
      meta?.setAttribute('content', primaryCss(value));
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ h, c }));
    } else {
      root.removeProperty('--brand-h');
      root.removeProperty('--brand-c');
      meta?.setAttribute('content', primaryCss(DEFAULT_BRAND));
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage refused (private mode, quota): the theme still applies, it just
    // is not remembered for the next load.
  }
}

/** The colour currently in force, for content that needs a hex (a new box). */
export const currentBrand = (): string => applied ?? DEFAULT_BRAND;

